/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  SocialLinksUpdateInput,
  SocialsPlatformServiceName,
  Vendor,
  VendorUpdateInput
} from "@bluframe/mightychroma-mightytool-frontend-types"
import slugify from "@sindresorhus/slugify"
import { socialsPlatformServiceNameLabels } from "config/socialsPlatforms"
import useItemView, { UseItemViewReturn } from "hooks/useListItemView"
import {
  createRef,
  FormEventHandler,
  RefCallback,
  RefObject,
  useCallback,
  useMemo,
  useRef
} from "react"

import Item from "./Item"

export interface Props {
  isAdd?: boolean
  isAdmin?: boolean
  isEditing?: boolean
  isEditor?: boolean
  isSaveLoading?: boolean
  item?: Vendor
  onCancel: () => void
  onDelete?: () => void
  onEdit?: () => void
  onPublishToggle?: () => void
  onSave: (
    input: VendorUpdateInput,

    socialsLinks?: SocialLinksUpdateInput
  ) => void
}

export interface ComponentProps
  extends
    Omit<
      Props,
      | "isEditing"
      | "isSaveLoading"
      | "onCancel"
      | "onDelete"
      | "onEdit"
      | "onPublishToggle"
      | "onSave"
    >,
    Omit<UseItemViewReturn, "item" | "refs"> {
  bioRef: RefCallback<HTMLTextAreaElement>
  countryIdRef: RefCallback<HTMLInputElement>
  logoImageIdRef: RefCallback<HTMLInputElement>
  nameRef: RefCallback<HTMLInputElement>
  slugRef: RefCallback<HTMLInputElement>
  splashImageIdRef: RefCallback<HTMLInputElement>
  onGenerateSlug: () => void
  onSubmit: FormEventHandler
  socialsLinksRefs: Record<
    keyof SocialLinksUpdateInput,
    RefObject<HTMLInputElement | null>
  >
  socialsKeysPairs: { key: string; row: SocialsPlatformServiceName[] }[]
}

const SOCIAL_LINKS_PER_ROW = 2

const usePrepareComponent = ({
  isAdd,
  isEditing,
  isSaveLoading,
  item,
  onCancel,
  onDelete,
  onEdit,
  onPublishToggle,
  onSave,
  ...props
}: Props): ComponentProps => {
  const {
    actionButtonOnClick,
    actionButtonLabel,
    isActionButtonLoading,
    isEditMode,
    isModalOpen,
    modalContent,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalPublishToggleConfirm,
    outlinedButtonLabel,
    outlinedButtonOnClick
  } = useItemView<Vendor>({
    fields: [],
    isAdd,
    isEditing,
    isSaveLoading,
    item,
    onCancel,
    onDelete,
    onEdit,
    onPublishToggle
  })

  const bioInputRef = useRef<HTMLTextAreaElement | null>(null)
  const countryIdInputRef = useRef<HTMLInputElement | null>(null)
  const logoImageIdInputRef = useRef<HTMLInputElement | null>(null)
  const nameInputRef = useRef<HTMLInputElement | null>(null)
  const slugInputRef = useRef<HTMLInputElement | null>(null)
  const splashImageIdInputRef = useRef<HTMLInputElement | null>(null)

  const bioRef = useCallback<RefCallback<HTMLTextAreaElement>>((node) => {
    bioInputRef.current = node
  }, [])

  const countryIdRef = useCallback<RefCallback<HTMLInputElement>>((node) => {
    countryIdInputRef.current = node
  }, [])

  const logoImageIdRef = useCallback<RefCallback<HTMLInputElement>>((node) => {
    logoImageIdInputRef.current = node
  }, [])

  const nameRef = useCallback<RefCallback<HTMLInputElement>>((node) => {
    nameInputRef.current = node
  }, [])

  const slugRef = useCallback<RefCallback<HTMLInputElement>>((node) => {
    slugInputRef.current = node
  }, [])

  const splashImageIdRef = useCallback<RefCallback<HTMLInputElement>>(
    (node) => {
      splashImageIdInputRef.current = node
    },
    []
  )

  const socialsKeys = Object.keys(
    socialsPlatformServiceNameLabels
  ) as SocialsPlatformServiceName[]

  const socialsKeysPairs: ComponentProps["socialsKeysPairs"] = []

  for (
    let index = 0;
    index < socialsKeys.length;
    index += SOCIAL_LINKS_PER_ROW
  ) {
    const key = socialsKeys.slice(index, index + SOCIAL_LINKS_PER_ROW).join("-")

    const row = socialsKeys.slice(index, index + SOCIAL_LINKS_PER_ROW)
    socialsKeysPairs.push({ key, row })
  }

  const socialsLinksRefs: Record<
    keyof SocialLinksUpdateInput,
    RefObject<HTMLInputElement | null>
  > = useMemo(
    () => ({
      blueSky: createRef(),
      facebook: createRef(),
      instagram: createRef(),
      reddit: createRef(),
      threads: createRef(),
      twitterX: createRef(),
      website: createRef(),
      youtube: createRef()
    }),
    []
  )

  const onGenerateSlug = useCallback(() => {
    if (!nameInputRef.current || !slugInputRef.current) return
    const newSlug = slugify(nameInputRef.current.value || "")
    slugInputRef.current.value = newSlug
    slugInputRef.current.dispatchEvent(new Event("input", { bubbles: true }))
  }, [])

  const onSubmit: FormEventHandler = useCallback(
    (event) => {
      event.preventDefault()
      if (!nameInputRef.current?.value || !slugInputRef.current?.value) {
        return
      }

      const socialsLinks: SocialLinksUpdateInput = {}

      Object.keys(socialsLinksRefs).forEach((key) => {
        const ref = socialsLinksRefs[key as keyof SocialLinksUpdateInput]
        if (ref.current?.value) {
          socialsLinks[key as keyof SocialLinksUpdateInput] = ref.current.value
        }
      })

      onSave(
        {
          bio: bioInputRef.current?.value || undefined,
          countryId: countryIdInputRef.current?.value ?? undefined,
          logoImageId: logoImageIdInputRef.current?.value
            ? Number(logoImageIdInputRef.current.value)
            : undefined,
          name: nameInputRef.current.value,
          slug: slugInputRef.current.value,
          splashImageId: splashImageIdInputRef.current?.value
            ? Number(splashImageIdInputRef.current.value)
            : undefined
        },
        socialsLinks
      )
    },
    [onSave, socialsLinksRefs]
  )

  return {
    ...props,
    actionButtonLabel,
    actionButtonOnClick,
    bioRef,
    countryIdRef,
    isActionButtonLoading,
    isAdd,
    isEditMode,
    isModalOpen,
    item,
    logoImageIdRef,
    modalContent,
    nameRef,
    onGenerateSlug,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalPublishToggleConfirm,
    onSubmit,
    outlinedButtonLabel,
    outlinedButtonOnClick,
    slugRef,
    socialsKeysPairs,
    socialsLinksRefs,
    splashImageIdRef
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  Item
)
